import React, { useState, useEffect } from 'react'
import { Box, Typography, IconButton, Collapse, Alert } from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'

const NotificationContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 80,
  left: 24,
  zIndex: 1500,
  maxWidth: 420,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  [theme.breakpoints.down('md')]: {
    top: 70,
    left: 12,
    right: 12,
    maxWidth: 'calc(100vw - 24px)',
  },
}))

const NotificationItem = styled(Alert)(({ severity }) => ({
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)',
  backdropFilter: 'blur(10px)',
  border: '2px solid',
  borderColor:
    severity === 'success'
      ? 'rgba(16, 185, 129, 0.3)'
      : severity === 'error'
      ? 'rgba(239, 68, 68, 0.3)'
      : severity === 'warning'
      ? 'rgba(245, 158, 11, 0.3)'
      : 'rgba(103, 121, 227, 0.3)',
  animation: 'slideInFromLeft 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '@keyframes slideInFromLeft': {
    from: {
      opacity: 0,
      transform: 'translateX(-100px)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  '& .MuiAlert-icon': {
    fontSize: 24,
  },
  '& .MuiAlert-message': {
    fontSize: 14,
    fontWeight: 500,
    padding: '4px 0',
  },
}))

let notificationId = 0

export const NotificationPanel = ({ onNotificationChange }) => {
  const [notifications, setNotifications] = useState([])

  useEffect(() => {
    // Expose addNotification globally
    window.addNotification = (message, type = 'info', duration = 5000) => {
      const id = ++notificationId
      const newNotification = { id, message, type, duration }
      
      setNotifications((prev) => [...prev, newNotification])

      if (duration > 0) {
        setTimeout(() => {
          removeNotification(id)
        }, duration)
      }
    }

    return () => {
      delete window.addNotification
    }
  }, [])

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  return (
    <NotificationContainer>
      {notifications.map((notification) => (
        <Collapse key={notification.id} in={true}>
          <NotificationItem
            severity={notification.type}
            icon={
              notification.type === 'success' ? (
                <CheckCircleIcon />
              ) : notification.type === 'error' ? (
                <ErrorIcon />
              ) : notification.type === 'warning' ? (
                <WarningIcon />
              ) : (
                <InfoIcon />
              )
            }
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => removeNotification(notification.id)}
                sx={{ padding: '4px' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {notification.message}
          </NotificationItem>
        </Collapse>
      ))}
    </NotificationContainer>
  )
}

// Helper functions to replace toast
export const showNotification = {
  success: (message, duration = 5000) => {
    if (window.addNotification) {
      window.addNotification(message, 'success', duration)
    }
  },
  error: (message, duration = 5000) => {
    if (window.addNotification) {
      window.addNotification(message, 'error', duration)
    }
  },
  warning: (message, duration = 5000) => {
    if (window.addNotification) {
      window.addNotification(message, 'warning', duration)
    }
  },
  info: (message, duration = 5000) => {
    if (window.addNotification) {
      window.addNotification(message, 'info', duration)
    }
  },
}

export default NotificationPanel

